import React, { ReactNode, useCallback } from 'react'
import { useKey } from 'react-use'
import { IS_EMBEDDED } from '@sketch/constants'
import { useResponsiveDropdown } from '@sketch/components'
import { keyWithoutModifier, keyCodeWithShift } from '@sketch/utils'
import * as S from './ResizeModeSelector.styles'
import { usePrototypeContext } from '../../hooks'
import {
  PrototypeResizeMode,
  usePrototypeZoom,
} from '@sketch-hq/sketch-web-renderer'

export function ResizeModeSelector() {
  const { setResizeMode } = usePrototypeContext()
  const prototypeScalePercent = Math.round(usePrototypeZoom() * 100)

  const setToActualSize = useCallback(
    () => setResizeMode(PrototypeResizeMode.ActualSize),
    [setResizeMode]
  )

  const setToFit = useCallback(() => setResizeMode(PrototypeResizeMode.Fit), [
    setResizeMode,
  ])

  const setToFitWidth = useCallback(
    () => setResizeMode(PrototypeResizeMode.FitWidth),
    [setResizeMode]
  )

  useKey(keyWithoutModifier('z'), setToActualSize)
  useKey(keyCodeWithShift('Digit0'), setToFit)
  useKey(keyCodeWithShift('Digit1'), setToFitWidth)

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ResizeModeDropdown,
    dropdownProps: {
      setToActualSize,
      setToFit,
      setToFitWidth,
    },
    placement: 'bottom-end',
    usePortal: true,
  })

  return (
    <>
      <S.Button
        variant="strong"
        label={`${prototypeScalePercent}%`}
        fullWidth
        data-testid="resize-mode-selector"
        chevrons
        {...buttonProps}
      />
      {content}
    </>
  )
}

type ResizeModeDropdownProps = {
  setToActualSize: () => void
  setToFit: () => void
  setToFitWidth: () => void
}

function ResizeModeDropdown(props: ResizeModeDropdownProps) {
  const { resizeMode } = usePrototypeContext()
  return (
    <>
      <ResizeModeDropdownItem
        onClick={props.setToActualSize}
        active={resizeMode === PrototypeResizeMode.ActualSize}
        keyboardShortCutHint="z"
      >
        Actual Size
      </ResizeModeDropdownItem>
      <ResizeModeDropdownItem
        onClick={props.setToFit}
        active={resizeMode === PrototypeResizeMode.Fit}
        keyboardShortCutHint="Shift 0"
      >
        Fit Canvas
      </ResizeModeDropdownItem>
      <ResizeModeDropdownItem
        onClick={props.setToFitWidth}
        active={resizeMode === PrototypeResizeMode.FitWidth}
        keyboardShortCutHint="Shift 1"
      >
        Fit Canvas Width
      </ResizeModeDropdownItem>
    </>
  )
}

type ResizeModeDropdownItemProps = {
  active: boolean
  children: ReactNode
  onClick: () => void
  keyboardShortCutHint: string
}

function ResizeModeDropdownItem(props: ResizeModeDropdownItemProps) {
  return (
    <S.Item onClick={props.onClick}>
      <S.StyledRadioButton
        checked={props.active}
        // noop to avoid console warning (we are using onClick handler instead)
        onChange={() => {}}
      />
      <S.Label>{props.children}</S.Label>
      {!IS_EMBEDDED && (
        <S.KeyboardShortcut>{props.keyboardShortCutHint}</S.KeyboardShortcut>
      )}
    </S.Item>
  )
}
